<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-contact-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3"
            >mdi-card-account-details</v-icon
          >
          Edit Contact Information
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Update your agency's primary contact details
        </div>
      </v-card-text>

      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="
              messageType === 'success'
                ? 'mdi-check-circle'
                : 'mdi-alert-circle'
            "
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Contact Form -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.email"
              label="Email Address*"
              outlined
              dense
              prepend-inner-icon="mdi-email-outline"
              :rules="[rules.required, rules.email]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
          <v-col cols="12" md="6" class="py-2">
            <AppPhoneNumberInput
              v-model="profile.phone"
              @input="onChange"
              @update="onUpdatePhone"
              class="modern-phone-input"
            />
            <div
              v-if="phonePayload.isValid === false"
              class="text-caption error--text mt-1"
            >
              Please enter a valid phone number
            </div>
          </v-col>
          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.contact_person"
              label="Contact Person*"
              outlined
              dense
              prepend-inner-icon="mdi-account"
              :rules="[rules.required]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="profile.contact_title"
              label="Contact Title*"
              outlined
              dense
              prepend-inner-icon="mdi-badge-account"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!formValid"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AppPhoneNumberInput from "@/components/common/AppPhoneNumberInput.vue";

export default {
  components: { AppPhoneNumberInput },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      phonePayload: {},
      rules: {
        required: (value) => !!value || "Required.",
        email: (value) => {
          if (!value || typeof value !== "string")
            return "Invalid email address";
          
          // Check for capital letters
          if (value !== value.toLowerCase()) {
            return "Email must be in lowercase";
          }
          
          // Split email into local and domain parts
          const atIndex = value.lastIndexOf("@");
          if (atIndex === -1 || atIndex === 0 || atIndex === value.length - 1) {
            return "Invalid email address";
          }
          
          const local = value.substring(0, atIndex);
          const domain = value.substring(atIndex + 1);
          
          // Check for whitespace anywhere in the email
          if (/\s/.test(value)) {
            return "Invalid email address";
          }
          
          // More restrictive local part validation - only allow basic lowercase characters
          if (
            !local ||
            local.length > 64 ||
            local.startsWith(".") ||
            local.endsWith(".") ||
            local.includes("..") ||
            !/^[a-z0-9]/.test(local) || // must start with lowercase alphanumeric
            !/[a-z0-9]$/.test(local) || // must end with lowercase alphanumeric
            !/^[a-z0-9._-]+$/.test(local) // only allow lowercase letters, numbers, dots, underscores, and hyphens
          ) {
            return "Email can only contain lowercase letters, numbers, dots, underscores, and hyphens";
          }
          
          // Domain part validation (domains are case-insensitive, but we'll enforce lowercase)
          if (
            !domain ||
            domain.length > 253 ||
            domain.startsWith("-") ||
            domain.endsWith("-") ||
            domain.startsWith(".") ||
            domain.endsWith(".") ||
            domain.includes("..") ||
            !/^[a-z0-9.-]+$/.test(domain) || // allowed characters in domain (lowercase only)
            !/\.[a-z]{2,}$/.test(domain) // must end with valid lowercase TLD
          ) {
            return "Invalid email domain";
          }
          
          // Check domain parts (between dots) are valid
          const domainParts = domain.split(".");
          for (const part of domainParts) {
            if (
              !part ||
              part.length > 63 ||
              part.startsWith("-") ||
              part.endsWith("-") ||
              !/^[a-z0-9-]+$/.test(part) // lowercase only
            ) {
              return "Invalid email domain";
            }
          }
          
          return true;
        },
      },
      profile: {}
    };
  },
  computed: {
    ...mapState("auth", {
      myProfile: "profile"
    }),
    formValid() {
      // Check all required fields are filled
      const hasRequiredFields = (
        this.profile.email &&
        this.profile.phone &&
        this.profile.contact_person &&
        this.profile.contact_title
      );
      
      // Check email validation specifically
      const isEmailValid = this.profile.email && this.rules.email(this.profile.email) === true;
      
      // Check phone validation
      const isPhoneValid = this.phonePayload.isValid !== false;
      
      return hasRequiredFields && isEmailValid && isPhoneValid;
    }
  },
  methods: {
    ...mapActions("auth", {
      updateProfile: "updateProfile"
    }),
    onChange() {
      this.message = null;
    },
    onUpdatePhone(payload) {
      this.phonePayload = payload;
    },
    async onSave() {
      if (!this.formValid) {
        const emailValidation = this.rules.email(this.profile.email);
        if (emailValidation !== true) {
          this.message = emailValidation; // Show specific email error
        } else if (this.phonePayload.isValid === false) {
          this.message = "Please enter a valid phone number";
        } else {
          this.message = "Please complete all required fields correctly";
        }
        this.messageType = "error";
        return;
      }

      this.loading = true;
      try {
        let phone = this.profile.phone;
        if (
          this.phonePayload.isValid &&
          this.phonePayload.countryCallingCode != "1"
        ) {
          phone = `+${this.phonePayload.countryCallingCode} ${phone}`;
        }

        await this.updateProfile({
          email: this.profile.email,
          phone,
          contact_person: this.profile.contact_person,
          contact_title: this.profile.contact_title
        });

        this.message = "Contact information updated successfully!";
        this.messageType = "success";
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        if (error.response) {
          if (error.response.status === 401) {
            this.message = "Session expired. Please log in again.";
          } else {
            this.message = error.response.data.message || "An error occurred";
          }
        } else {
          this.message = error.message || "An error occurred";
        }
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
  }
};
</script>

<style scoped>
.modern-contact-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 1px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.modern-phone-input::v-deep .v-input__slot {
  border-radius: 8px !important;
  border: 1px solid #e0e0e0;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-contact-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-contact-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-contact-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-contact-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>
